import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from './ui';

const LT_LANG_MAP: Record<string, string> = {
  ja: 'ja',
  en: 'en',
  zh: 'zh',
  ko: 'ko',
  de: 'de',
  fr: 'fr',
  it: 'it',
  tr: 'tr',
  es: 'es',
};

async function fetchTranslation(text: string, target: string): Promise<string> {
  const res = await fetch(`/api/v1/translate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: text, source: 'auto', target }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.translatedText as string;
}

export const TranslateText: React.FC<{
  text: string;
  className?: string;
  inline?: boolean;
}> = ({ text, className = '', inline = false }) => {
  const { t, i18n } = useTranslation();
  const [translated, setTranslated] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  const [error, setError] = useState(false);

  const targetLang = LT_LANG_MAP[i18n.language] || i18n.language;

  const handleToggle = async () => {
    if (showTranslated) {
      setShowTranslated(false);
      return;
    }
    if (translated) {
      setShowTranslated(true);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const result = await fetchTranslation(text, targetLang);
      setTranslated(result);
      setShowTranslated(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const BtnWrap = inline ? 'span' : 'div';

  return (
    <span className={className}>
      {showTranslated ? translated || text : text}
      <BtnWrap className={inline ? 'ml-2 inline-flex align-middle' : 'mt-1.5 flex'}>
        <button
          onClick={handleToggle}
          disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-blue-500 hover:text-brand-blue-600 dark:text-brand-blue-300 dark:hover:text-brand-blue-200 transition disabled:opacity-50"
          title={showTranslated ? t('common.showOriginal') : t('common.translate')}
        >
          {loading ? (
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-full border-2 border-brand-blue-200 border-t-brand-blue-500 animate-spin" />
              {t('common.translating')}
            </span>
          ) : (
            <>
              <Icons.Globe width={12} height={12} />
              {showTranslated ? t('common.showOriginal') : t('common.translate')}
            </>
          )}
        </button>
        {error && (
          <span className="ml-2 text-[11px] text-rose-500">{t('common.translateError')}</span>
        )}
      </BtnWrap>
    </span>
  );
};
